/**
 * IrisChat — The primary editing interface for Builder V2.
 * Replaces the 6-tab sidebar with a conversational AI assistant.
 * Iris suggests next actions after every edit.
 *
 * LIGHT WARM THEME: #faf8f5 bg, #2a2725 text, #C9A84C accent
 */
import { useState, useEffect, useRef, memo } from 'react';
import { Send, Loader2, Sparkles, Globe, Image as ImageIcon, Palette, Search, FileText, Type, ArrowRight, RotateCcw } from 'lucide-react';

const IrisChat = memo(({
  chatHistory, sending, onSendMessage, onQuickAction,
  siteState, userPlan, onUpgrade, onPublish, publishing,
  publishStatus, onUndo, canUndo,
}) => {
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const [input, setInput] = useState('');

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory, sending]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    onSendMessage(input.trim());
    setInput('');
  };

  // Determine what chips to show based on what the user hasn't done yet
  const getChips = () => {
    const chips = [];
    if (!siteState?.has_personalized_copy) {
      chips.push({ label: 'Personalize my copy', icon: Type, action: 'personalize_copy' });
    }
    if (!siteState?.has_uploaded_photos) {
      chips.push({ label: 'Swap my photos', icon: ImageIcon, action: 'swap_photos' });
    }
    chips.push({ label: 'Change colors', icon: Palette, action: 'change_colors' });
    if (!siteState?.has_run_seo) {
      chips.push({ label: 'SEO audit', icon: Search, action: 'seo_audit' });
    }
    if (!siteState?.has_blog) {
      chips.push({ label: 'Write blog posts', icon: FileText, action: 'write_blogs' });
    }
    return chips.slice(0, 4);
  };

  const chips = getChips();

  // Edit limit warning
  const editsUsed = userPlan?.edits?.used || 0;
  const editsLimit = userPlan?.edits?.limit || 30;
  const editsRemaining = userPlan?.features?.unlimited_edits ? Infinity : (editsLimit - editsUsed);
  const showWarning = editsRemaining <= 5 && editsRemaining > 0 && !userPlan?.features?.unlimited_edits;

  return (
    <div style={{
      width: '400px', display: 'flex', flexDirection: 'column', flexShrink: 0,
      background: '#f5f2ee', borderRight: '1px solid #e8e3dc',
    }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #e8e3dc', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(201,168,76,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Sparkles size={16} style={{ color: '#C9A84C' }} />
          </div>
          <div>
            <p style={{ fontFamily: 'var(--sans)', fontSize: '15px', fontWeight: 600, color: '#2a2725' }}>Iris</p>
            <p style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: '#8c8782' }}>Your AI design partner</p>
          </div>
        </div>
        {/* Publish button in header */}
        {publishStatus?.status === 'live' ? (
          <a href={publishStatus.urls?.subdomain_url || '#'} target="_blank" rel="noopener noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 14px', background: 'rgba(91,168,157,0.08)', border: '1px solid rgba(91,168,157,0.15)', borderRadius: '8px', fontFamily: 'var(--sans)', fontSize: '12px', color: '#5ba89d', textDecoration: 'none', fontWeight: 500 }}>
            <Globe size={13} /> Live
          </a>
        ) : (
          <button onClick={onPublish} disabled={publishing}
            style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 14px', background: '#C9A84C', border: 'none', borderRadius: '8px', fontFamily: 'var(--sans)', fontSize: '12px', color: '#fff', cursor: publishing ? 'wait' : 'pointer', fontWeight: 600, boxShadow: '0 2px 8px rgba(201,168,76,0.2)', transition: 'all .15s' }}>
            {publishing ? <Loader2 size={13} className="animate-spin" /> : <Globe size={13} />} Publish
          </button>
        )}
      </div>

      {/* Chat history */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
        {chatHistory.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(201,168,76,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Sparkles size={22} style={{ color: '#C9A84C' }} />
            </div>
            <p style={{ fontFamily: 'var(--sans)', fontSize: '16px', fontWeight: 600, color: '#2a2725', marginBottom: '6px' }}>
              Welcome! I'm Iris.
            </p>
            <p style={{ fontFamily: 'var(--sans)', fontSize: '14px', color: '#8c8782', lineHeight: 1.6, maxWidth: '280px', margin: '0 auto 20px' }}>
              I built this site from scratch for you. Tell me what to change — colors, copy, layout, photos, anything. Or try a suggestion below.
            </p>
            {/* Site readiness checklist */}
            {siteState && (
              <div data-testid="site-readiness" style={{ textAlign: 'left', background: '#fff', borderRadius: '12px', padding: '14px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', maxWidth: '300px', margin: '0 auto' }}>
                <p style={{ fontFamily: 'var(--sans)', fontSize: '11px', fontWeight: 600, color: '#8c8782', letterSpacing: '.04em', textTransform: 'uppercase', marginBottom: '10px' }}>Ready to publish?</p>
                {[
                  { done: siteState.has_personalized_copy, label: 'Copy personalized' },
                  { done: siteState.has_uploaded_photos, label: 'Real photos added' },
                  { done: siteState.has_run_seo, label: 'SEO optimized' },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <div style={{
                      width: '16px', height: '16px', borderRadius: '50%', flexShrink: 0,
                      background: item.done ? 'rgba(91,168,157,0.1)' : 'rgba(0,0,0,0.04)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {item.done ? (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#5ba89d" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      ) : (
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#e8e3dc' }} />
                      )}
                    </div>
                    <span style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: item.done ? '#5ba89d' : '#8c8782', textDecoration: item.done ? 'line-through' : 'none' }}>{item.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {chatHistory.map((msg, i) => (
          <div key={i} className="builder-msg-enter" style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: '12px', animationDelay: `${i * 0.05}s` }}>
            <div style={{
              maxWidth: '85%', padding: '12px 16px',
              background: msg.role === 'user' ? '#C9A84C' : '#fff',
              color: msg.role === 'user' ? '#fff' : '#2a2725',
              borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
              fontFamily: 'var(--sans)', fontSize: '14px', lineHeight: 1.6,
              boxShadow: msg.role === 'user' ? 'none' : '0 1px 3px rgba(0,0,0,0.04)',
            }}>
              {msg.role === 'assistant' ? (
                <div dangerouslySetInnerHTML={{ __html: msg.message
                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                  .replace(/\n- /g, '<br/>• ')
                  .replace(/\n\n/g, '<br/><br/>')
                  .replace(/\n/g, '<br/>')
                }} />
              ) : msg.message}
              {msg.suggestions && (
                <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {msg.suggestions.map((s, j) => (
                    <button key={j} onClick={() => onQuickAction(s.action)} style={{
                      width: '100%', padding: '10px 14px', background: '#faf8f5', border: '1px solid #e8e3dc',
                      borderRadius: '10px', cursor: 'pointer', textAlign: 'left', transition: 'all .15s',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      fontFamily: 'var(--sans)', fontSize: '13px', color: '#2a2725', fontWeight: 500,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#C9A84C'; e.currentTarget.style.background = 'rgba(201,168,76,0.03)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8e3dc'; e.currentTarget.style.background = '#faf8f5'; }}
                    >
                      {s.label} <ArrowRight size={14} style={{ color: '#C9A84C' }} />
                    </button>
                  ))}
                </div>
              )}
              {/* Inline undo — show on the last assistant message when undo is available */}
              {msg.role === 'assistant' && canUndo && i === chatHistory.length - 1 && !msg.suggestions && (
                <button onClick={onUndo} data-testid="iris-inline-undo" style={{
                  marginTop: '10px', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '5px',
                  background: '#faf8f5', border: '1px solid #e8e3dc', borderRadius: '8px',
                  fontFamily: 'var(--sans)', fontSize: '12px', color: '#8c8782', cursor: 'pointer',
                  transition: 'all .15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#C9A84C'; e.currentTarget.style.color = '#C9A84C'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8e3dc'; e.currentTarget.style.color = '#8c8782'; }}
                >
                  <RotateCcw size={12} /> Undo this change
                </button>
              )}
            </div>
          </div>
        ))}

        {sending && (
          <div className="builder-msg-enter" style={{ display: 'flex', marginBottom: '12px' }}>
            <div style={{ padding: '14px 18px', background: '#fff', borderRadius: '16px 16px 16px 4px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span className="typing-dots"><span /><span /><span /></span>
              <span data-testid="iris-thinking-status" style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: '#8c8782' }}>Iris is designing your changes...</span>
            </div>
          </div>
        )}

        {/* Edit limit warning */}
        {showWarning && (
          <div style={{ padding: '12px 14px', background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: '10px', marginBottom: '12px' }}>
            <p style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: '#C9A84C', fontWeight: 500, marginBottom: '4px' }}>
              {editsRemaining} edit{editsRemaining !== 1 ? 's' : ''} remaining this month
            </p>
            <p style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: '#8c8782', marginBottom: '8px' }}>
              Upgrade for unlimited edits, or your allowance renews next month.
            </p>
            <button onClick={onUpgrade} style={{
              padding: '6px 14px', background: '#C9A84C', color: '#fff', border: 'none',
              borderRadius: '8px', fontFamily: 'var(--sans)', fontSize: '12px', fontWeight: 500, cursor: 'pointer',
            }}>
              Upgrade to Pro
            </button>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Quick action chips */}
      {chips.length > 0 && !sending && (
        <div style={{ padding: '8px 20px 0', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {chips.map((chip, i) => (
            <button key={chip.action} onClick={() => onQuickAction(chip.action)} className="builder-chip-enter" style={{
              padding: '6px 14px', display: 'flex', alignItems: 'center', gap: '5px',
              background: '#fff', border: '1px solid #e8e3dc', borderRadius: '9999px',
              fontFamily: 'var(--sans)', fontSize: '12px', color: '#2a2725', fontWeight: 500,
              cursor: 'pointer', transition: 'all .15s',
              animationDelay: `${i * 0.08}s`, animationFillMode: 'backwards',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#C9A84C'; e.currentTarget.style.color = '#C9A84C'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8e3dc'; e.currentTarget.style.color = '#2a2725'; }}
            >
              <chip.icon size={13} /> {chip.label}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div style={{ padding: '12px 20px 16px' }}>
        <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '8px' }}>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Ask Iris anything..."
            disabled={sending}
            data-testid="iris-chat-input"
            style={{
              flex: 1, padding: '14px 16px', fontSize: '14px', fontFamily: 'var(--sans)',
              background: '#fff', border: '1px solid #e8e3dc', color: '#2a2725',
              outline: 'none', borderRadius: '12px', transition: 'border-color .2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}
            onFocus={e => e.currentTarget.style.borderColor = '#C9A84C'}
            onBlur={e => e.currentTarget.style.borderColor = '#e8e3dc'}
          />
          <button type="submit" disabled={sending || !input.trim()} data-testid="iris-send-btn" style={{
            width: '48px', height: '48px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: input.trim() ? '#C9A84C' : '#e8e3dc', color: input.trim() ? '#fff' : '#8c8782',
            border: 'none', cursor: input.trim() ? 'pointer' : 'default', borderRadius: '12px',
            transition: 'all .15s', boxShadow: input.trim() ? '0 2px 8px rgba(201,168,76,0.2)' : 'none',
          }}>
            {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </button>
        </form>
      </div>
    </div>
  );
});

IrisChat.displayName = 'IrisChat';
export default IrisChat;
